import type { ReportGroup } from '@et/shared';
import { useState } from 'react';
import { money, signed } from '../lib/format';
import { useT } from '../lib/i18n';

/**
 * Report charts (dataviz reference method): one axis, diverging blue (gain) ↔ red (loss)
 * around a gray zero line, 2 px gaps between bars, 4 px rounded ends, hover tooltips,
 * direct value labels on the per-coin bars so identity never rests on color alone.
 * Numbers are converted only to place pixels; every figure shown is the original string.
 */

const px = (v: string): number => Number.parseFloat(v);

function Tip({ g, title }: { g: ReportGroup; title: string }): JSX.Element {
  const t = useT();
  return (
    <>
      <div className="muted">{title}</div>
      <div className="num"><b>{t('Net')}: {signed(g.net)} USDT</b></div>
      <div className="num pos">{t('Gain')}: {signed(g.profit)}</div>
      <div className="num neg">{t('Loss')}: {money(g.loss)}</div>
      <div className="num muted">{t('Fees')}: {money(g.fees)}</div>
      <div className="muted">{t('{n} trades · {w} won · {l} lost', { n: g.trades, w: g.wins, l: g.losses })}</div>
    </>
  );
}

/** Net result per day / week / month. */
export function PeriodBars({ rows, labelOf }: { rows: (ReportGroup & { period: string })[]; labelOf: (period: string) => string }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const t = useT();
  if (rows.length === 0) return <div className="empty">{t('No closed trades in this range.')}</div>;

  const W = 720;
  const H = 200;
  const L = 56;
  const vals = rows.map((r) => px(r.net));
  const max = Math.max(...vals.map(Math.abs), 1e-9);
  const zeroY = H / 2;
  const slot = (W - L - 8) / rows.length;
  const barW = Math.max(3, Math.min(36, slot - 2));

  return (
    <div className="chart-box">
      <svg className="chart" viewBox={`0 0 ${String(W)} ${String(H + 22)}`} role="img" aria-label={t('Net profit or loss per period')} onPointerLeave={() => { setHover(null); }}>
        <line className="gridline" x1={L} x2={W - 8} y1={8} y2={8} />
        <line className="gridline" x1={L} x2={W - 8} y1={H - 8} y2={H - 8} />
        <text className="axis-label" x={L - 6} y={12} textAnchor="end">{money(max.toFixed(2), 0)}</text>
        <text className="axis-label" x={L - 6} y={H - 4} textAnchor="end">−{money(max.toFixed(2), 0)}</text>
        <line className="zero" x1={L} x2={W - 8} y1={zeroY} y2={zeroY} />
        <text className="axis-label" x={L - 6} y={zeroY + 4} textAnchor="end">0</text>
        {rows.map((r, i) => {
          const v = vals[i] ?? 0;
          const len = (Math.abs(v) / max) * (zeroY - 10);
          const x = L + i * slot + (slot - barW) / 2;
          return (
            <g key={r.period} onPointerEnter={() => { setHover(i); }}>
              <rect x={L + i * slot} y={0} width={slot} height={H} fill="transparent" />
              <rect className={v >= 0 ? 'bar-pos' : 'bar-neg'} x={x} y={v >= 0 ? zeroY - len : zeroY} width={barW} height={Math.max(len, 1)} rx={Math.min(4, barW / 2)} />
            </g>
          );
        })}
        <text className="axis-label" x={L} y={H + 16}>{labelOf(rows[0]?.period ?? '')}</text>
        <text className="axis-label" x={W - 8} y={H + 16} textAnchor="end">{labelOf(rows[rows.length - 1]?.period ?? '')}</text>
      </svg>
      {hover !== null && rows[hover] && (
        <div className="chart-tip" style={{ left: `${String(((L + (hover + 0.5) * slot) / W) * 100)}%`, top: '15%' }}>
          <Tip g={rows[hover]} title={labelOf(rows[hover].period)} />
        </div>
      )}
    </div>
  );
}

/** Net result per coin, best first; horizontal so coin names stay readable. */
export function CoinBars({ rows }: { rows: (ReportGroup & { symbol: string })[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const t = useT();
  if (rows.length === 0) return <div className="empty">{t('No closed trades in this range.')}</div>;

  const W = 720;
  const ROW = 28;
  const LABEL = 96;
  const VALUE = 96;
  const H = rows.length * ROW + 8;
  const vals = rows.map((r) => px(r.net));
  const max = Math.max(...vals.map(Math.abs), 1e-9);
  const hasNeg = vals.some((v) => v < 0);
  const hasPos = vals.some((v) => v > 0);
  const plotL = LABEL;
  const plotR = W - VALUE;
  const zeroX = hasNeg && hasPos ? (plotL + plotR) / 2 : hasNeg ? plotR : plotL;
  const span = hasNeg && hasPos ? (plotR - plotL) / 2 : plotR - plotL;

  return (
    <div className="chart-box">
      <svg className="chart" viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={t('Net profit or loss per coin')} onPointerLeave={() => { setHover(null); }}>
        <line className="zero" x1={zeroX} x2={zeroX} y1={0} y2={H} />
        {rows.map((r, i) => {
          const v = vals[i] ?? 0;
          const len = (Math.abs(v) / max) * (span - 4);
          const y = 4 + i * ROW;
          const x = v >= 0 ? zeroX : zeroX - len;
          return (
            <g key={r.symbol} onPointerEnter={() => { setHover(i); }}>
              <rect x={0} y={y} width={W} height={ROW} fill="transparent" />
              <text className="axis-label" x={LABEL - 10} y={y + ROW / 2 + 4} textAnchor="end" style={{ fill: 'var(--text-secondary)' }}>{r.symbol.replace('/USDT', '')}</text>
              <rect className={v >= 0 ? 'bar-pos' : 'bar-neg'} x={x} y={y + 5} width={Math.max(len, 1)} height={ROW - 10} rx={4} />
              <text className="axis-label" x={W - 4} y={y + ROW / 2 + 4} textAnchor="end" style={{ fill: 'var(--text-primary)' }}>{signed(r.net)}</text>
            </g>
          );
        })}
      </svg>
      {hover !== null && rows[hover] && (
        <div className="chart-tip" style={{ left: '50%', top: `${String(((4 + hover * ROW) / H) * 100)}%` }}>
          <Tip g={rows[hover]} title={rows[hover].symbol} />
        </div>
      )}
    </div>
  );
}
